import {
  Archive,
  ArchiveRestore,
  Download,
  EllipsisVertical,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-mui";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { keys } from "../../../api/queryKeys.ts";
import { api, getErrorMessage, type ProspectListDetail } from "../../../api/client.ts";
import AgentActivity from "../../../components/AgentActivity.tsx";
import CollaboratorStack from "../../../components/CollaboratorStack.tsx";
import DropdownMenu from "../../../components/DropdownMenu.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import HubSpotLogo from "../../../components/HubSpotLogo.tsx";
import KpiTile from "../../../components/KpiTile.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import { collectionPath, useTenant } from "../../../contexts/TenantContext.tsx";
import CollectionOrigin from "../../../components/prospects/CollectionOrigin.tsx";
import SignalListChip from "./SignalListChip.tsx";

interface ListHeaderProps {
  list: ProspectListDetail;
  enrichableBlanks: number;
  enrichableConflicts: number;
  onEdit: () => void;
  onToggleArchive: () => void;
  onDelete: () => void;
  onOpenEnrich: () => void;
  onOpenExport: () => void;
  onOpenCrmExport: () => void;
  /** Add a company the database does not hold, by name. */
  onOpenAddByName: () => void;
  exportSlot: React.ReactNode;
}

export default function ListHeader({
  list,
  enrichableBlanks,
  enrichableConflicts,
  onEdit,
  onToggleArchive,
  onDelete,
  onOpenEnrich,
  onOpenExport,
  onOpenCrmExport,
  onOpenAddByName,
  exportSlot,
}: ListHeaderProps) {
  const { tenantPath } = useTenant();
  const navigate = useNavigate();
  const hasEnrichable = enrichableBlanks + enrichableConflicts > 0;

  // Resolve whether a CRM export is even possible so the menu item can stay
  // visible-but-disabled with a reason, rather than vanishing. A tenant with no
  // (or an ambiguous) CRM mapping is a config state, not a transient error, so
  // don't retry.
  const crmConnection = useQuery({
    queryKey: keys.prospectListCrmConnection.byList(list.id),
    queryFn: () => api.getProspectListCrmConnection(list.id),
    retry: false,
    // The failure surfaces inline as the menu item's disabled reason below, so
    // don't also fire the global error toast on every list-header render.
    meta: { suppressErrorToast: true },
  });
  const isEmpty = list.prospect_count === 0;
  // A sheet must be complete before it can be exported. The backend enforces
  // this too (409); disabling the export items surfaces it up front.
  const incompleteReason =
    list.incomplete_count > 0
      ? `${list.incomplete_count} entr${
          list.incomplete_count === 1 ? "y is" : "ies are"
        } missing required properties`
      : null;
  const crmExportDisabledReason = isEmpty
    ? "Add entries to this collection before exporting to the CRM"
    : crmConnection.isError
      ? getErrorMessage(crmConnection.error)
      : incompleteReason;
  // The state tiles open the companies in that state (the "Show" filter).
  const here = tenantPath(collectionPath(list.id));
  const tiles: { label: string; value: number; color?: "success" | "error"; to?: string }[] = [
    { label: "Total", value: list.prospect_count, to: here },
    { label: "Pending", value: list.entry_counts.pending, to: `${here}?state=pending` },
    { label: "Accepted", value: list.entry_counts.accepted, color: "success", to: `${here}?state=accepted` },
    {
      label: "Disqualified",
      value: list.entry_counts.disqualified,
      color: "error",
      to: `${here}?state=disqualified`,
    },
    { label: "Signal triggers", value: list.signal_trigger_count },
  ];
  return (
    <>
      <PageHeader
        back={{ to: tenantPath(collectionPath()), label: "Collections" }}
        title={list.name}
        description={
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            {list.description && <span>{list.description}</span>}
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
              {list.collection_type === "signal" && (
                <SignalListChip
                  onClick={() => navigate(tenantPath(`/prospects/inbox?show=all&collection_id=${list.id}`))}
                />
              )}
              <CollaboratorStack
                creator={
                  list.created_by_user_id
                    ? {
                        user_id: list.created_by_user_id,
                        display_name: list.created_by_display_name,
                        email: list.created_by_email,
                        picture_url: list.created_by_picture_url,
                      }
                    : null
                }
                collaborators={list.collaborators}
                maxVisible={5}
                size={24}
              />
              <span>
                Created <FormattedDate value={list.created_at} />
                {" · "}
                <CollectionOrigin origin={list.origin} variant="inline" />
              </span>
              <AgentActivity runs={list.active_agent_runs} showLabel />
            </Stack>
          </Stack>
        }
        actions={
          <>
            <Button variant="contained" startIcon={<Plus />} onClick={onOpenAddByName}>
              Add company
            </Button>
            <DropdownMenu aria-label="More collection actions" icon={<EllipsisVertical />} align="right" minWidth={240}>
              <MenuItem onClick={onEdit}>
                <ListItemIcon>
                  <Pencil fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit collection</ListItemText>
              </MenuItem>
              {hasEnrichable && (
                <MenuItem onClick={onOpenEnrich}>
                  <ListItemIcon>
                    <Sparkles fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    secondary={`${enrichableBlanks} blanks${
                      enrichableConflicts ? `, ${enrichableConflicts} conflicts` : ""
                    }`}
                  >
                    Enrich companies from labels
                  </ListItemText>
                </MenuItem>
              )}
              {!isEmpty && (
                <MenuItem onClick={onOpenExport} disabled={incompleteReason !== null}>
                  <ListItemIcon>
                    <Download fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    secondary={incompleteReason}
                    slotProps={{ secondary: { sx: { whiteSpace: "normal", maxWidth: 280 } } }}
                  >
                    Export to CSV
                  </ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={onOpenCrmExport} disabled={crmExportDisabledReason !== null}>
                <ListItemIcon>
                  {crmConnection.data?.provider === "hubspot" ? (
                    <HubSpotLogo fontSize="small" />
                  ) : (
                    <Upload fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  secondary={crmExportDisabledReason}
                  slotProps={{ secondary: { sx: { whiteSpace: "normal", maxWidth: 280 } } }}
                >
                  Export to CRM
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={onToggleArchive}>
                <ListItemIcon>
                  {list.archived ? <ArchiveRestore fontSize="small" /> : <Archive fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{list.archived ? "Restore collection" : "Archive collection"}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={onDelete} sx={{ color: "error.main" }}>
                <ListItemIcon sx={{ color: "inherit" }}>
                  <Trash2 fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete collection</ListItemText>
              </MenuItem>
            </DropdownMenu>
          </>
        }
      />
      {/* The CRM and CSV export dialogs, opened from the menu. */}
      {exportSlot}
      {incompleteReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {incompleteReason}. Fill in the missing fields or disqualify those
          entries before exporting.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((t) => (
          <Grid key={t.label} size={{ xs: 6, sm: 4, md: "grow" }}>
            <KpiTile dense label={t.label} value={t.value} color={t.color} emphasis={!!t.color} to={t.to} />
          </Grid>
        ))}
      </Grid>
    </>
  );
}
